/**
 * SupportDashboardPage - Platform Admin Support Center
 * "Diplomatic Precision" Design with Chat Modal
 *
 * A refined, authoritative support interface channeling
 * Ghanaian governmental institutions. Chat opens in a modal
 * for better UX and layout control.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/context';
import { notificationSSE } from '@/services/notifications';
import {
  getAllConversations,
  getConversationQueue,
  getAdminConversation,
  assignConversation,
  sendAdminMessage,
  resolveConversation,
  getStats,
  getCannedResponses,
  getPlatformCategories,
  trackCannedResponseUsage,
  type SupportConversation,
  type SupportMessage,
  type SupportCategory,
  type CannedResponse,
  type ConversationStats,
} from '@/services/support';
import styles from './SupportDashboardPage.module.css';

// Status badge colors - Ghana-inspired palette
const STATUS_COLORS: Record<string, string> = {
  open: '#f59e0b',
  assigned: '#006B3F',
  pending_user: '#8b5cf6',
  resolved: '#10b981',
  closed: '#64748b',
};

// Priority configuration
const PRIORITY_CONFIG: Record<string, { color: string; label: string }> = {
  urgent: { color: '#dc2626', label: 'Urgent' },
  high: { color: '#ea580c', label: 'High' },
  normal: { color: '#006B3F', label: 'Normal' },
  low: { color: '#64748b', label: 'Low' },
};

// Icons
const Icons = {
  messageCircle: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
    </svg>
  ),
  inbox: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/>
      <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/>
    </svg>
  ),
  checkCircle: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
      <polyline points="22 4 12 14.01 9 11.01"/>
    </svg>
  ),
  clock: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <polyline points="12 6 12 12 16 14"/>
    </svg>
  ),
  star: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
    </svg>
  ),
  user: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
      <circle cx="12" cy="7" r="4"/>
    </svg>
  ),
  send: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 2L11 13"/>
      <path d="M22 2L15 22L11 13L2 9L22 2Z"/>
    </svg>
  ),
  search: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8"/>
      <line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
  ),
  refresh: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 2v6h-6"/>
      <path d="M3 12a9 9 0 0 1 15-6.7L21 8"/>
      <path d="M3 22v-6h6"/>
      <path d="M21 12a9 9 0 0 1-15 6.7L3 16"/>
    </svg>
  ),
  filter: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
    </svg>
  ),
  x: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/>
      <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  ),
  zap: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
    </svg>
  ),
  building: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="2" width="16" height="20" rx="2" ry="2"/>
      <path d="M9 22v-4h6v4"/>
      <path d="M8 6h.01M16 6h.01M12 6h.01M12 10h.01M12 14h.01M16 10h.01M16 14h.01M8 10h.01M8 14h.01"/>
    </svg>
  ),
  sparkles: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l1.912 5.813a2 2 0 0 0 1.275 1.275L21 12l-5.813 1.912a2 2 0 0 0-1.275 1.275L12 21l-1.912-5.813a2 2 0 0 0-1.275-1.275L3 12l5.813-1.912a2 2 0 0 0 1.275-1.275L12 3z"/>
    </svg>
  ),
  headphones: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 18v-6a9 9 0 0 1 18 0v6"/>
      <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/>
    </svg>
  ),
  externalLink: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
      <polyline points="15 3 21 3 21 9"/>
      <line x1="10" y1="14" x2="21" y2="3"/>
    </svg>
  ),
};

// Time formatting
function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

function formatTime(dateString: string): string {
  return new Date(dateString).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

export function SupportDashboardPage() {
  const { user } = useAuth();

  // State
  const [conversations, setConversations] = useState<SupportConversation[]>([]);
  const [activeConversation, setActiveConversation] = useState<SupportConversation | null>(null);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [stats, setStats] = useState<ConversationStats | null>(null);
  const [categories, setCategories] = useState<SupportCategory[]>([]);
  const [cannedResponses, setCannedResponses] = useState<CannedResponse[]>([]);

  // Loading states
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);

  // Filters
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterPriority, setFilterPriority] = useState<string>('');
  const [filterCategory, setFilterCategory] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<'all' | 'queue' | 'mine'>('all');

  // Chat modal state
  const [showChatModal, setShowChatModal] = useState(false);
  const [messageInput, setMessageInput] = useState('');
  const [showCannedResponses, setShowCannedResponses] = useState(false);
  const [resolveNotes, setResolveNotes] = useState('');
  const [showResolveModal, setShowResolveModal] = useState(false);

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const activeConversationId = useRef<string | null>(null);

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    setLoadingConversations(true);
    try {
      let response;

      if (viewMode === 'queue') {
        response = await getConversationQueue();
      } else {
        const params: Record<string, string> = {};
        if (filterStatus) params.status = filterStatus;
        if (filterPriority) params.priority = filterPriority;
        if (filterCategory) params.categoryId = filterCategory;
        if (searchQuery) params.search = searchQuery;
        if (viewMode === 'mine' && user?.id) params.assignedTo = user.id;

        response = await getAllConversations(params);
      }

      if (response.success && response.data) {
        setConversations(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoadingConversations(false);
    }
  }, [viewMode, filterStatus, filterPriority, filterCategory, searchQuery, user?.id]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await getStats();
      if (response.success && response.data) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, []);

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const response = await getPlatformCategories();
      if (response.success && response.data) {
        setCategories(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  }, []);

  // Fetch canned responses
  const fetchCannedResponses = useCallback(async () => {
    try {
      const response = await getCannedResponses();
      if (response.success && response.data) {
        setCannedResponses(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch canned responses:', error);
    }
  }, []);

  // Open conversation in modal
  const openConversation = useCallback(async (id: string) => {
    setShowChatModal(true);
    setLoadingMessages(true);
    activeConversationId.current = id;

    try {
      const response = await getAdminConversation(id);
      if (response.success && response.data) {
        setActiveConversation(response.data.conversation);
        setMessages(response.data.messages);
      }
    } catch (error) {
      console.error('Failed to load conversation:', error);
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  // Close chat modal
  const closeChatModal = useCallback(() => {
    setShowChatModal(false);
    setActiveConversation(null);
    setMessages([]);
    setMessageInput('');
    setShowCannedResponses(false);
    activeConversationId.current = null;
  }, []);

  // Claim conversation
  const handleClaim = useCallback(async () => {
    if (!activeConversation || !user) return;

    try {
      const response = await assignConversation(
        activeConversation.id,
        user.id,
        `${user.firstName} ${user.lastName}`
      );

      if (response.success) {
        setActiveConversation(prev => prev ? {
          ...prev,
          status: 'assigned',
          assigned_to: user.id,
          assigned_to_name: `${user.firstName} ${user.lastName}`,
          assigned_at: new Date().toISOString(),
        } : null);

        setConversations(prev => prev.map(c =>
          c.id === activeConversation.id
            ? { ...c, status: 'assigned' as const, assigned_to: user.id, assigned_to_name: `${user.firstName} ${user.lastName}` }
            : c
        ));

        const systemMessage: SupportMessage = {
          id: 'system-' + Date.now(),
          conversation_id: activeConversation.id,
          sender_id: 'system',
          sender_name: 'System',
          sender_type: 'system',
          content: `${user.firstName} ${user.lastName} has claimed this conversation.`,
          message_type: 'status_change',
          attachment_url: null,
          attachment_name: null,
          attachment_type: null,
          is_read: 1,
          read_at: null,
          created_at: new Date().toISOString(),
        };
        setMessages(prev => [...prev, systemMessage]);
      }
    } catch (error) {
      console.error('Failed to claim conversation:', error);
    }
  }, [activeConversation, user]);

  // Send message
  const handleSendMessage = useCallback(async () => {
    if (!messageInput.trim() || !activeConversation) return;

    setSending(true);
    try {
      const response = await sendAdminMessage(activeConversation.id, {
        content: messageInput.trim(),
      });

      if (response.success && response.data) {
        setMessages(prev => [...prev, response.data as SupportMessage]);
        setMessageInput('');

        setConversations(prev => prev.map(c =>
          c.id === activeConversation.id
            ? { ...c, last_message_at: new Date().toISOString() }
            : c
        ));
      }
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setSending(false);
    }
  }, [messageInput, activeConversation]);

  // Use canned response
  const handleUseCannedResponse = useCallback(async (response: CannedResponse) => {
    setMessageInput(response.content);
    setShowCannedResponses(false);

    try {
      await trackCannedResponseUsage(response.id);
    } catch (error) {
      // Ignore tracking errors
    }
  }, []);

  // Resolve conversation
  const handleResolve = useCallback(async () => {
    if (!activeConversation) return;

    try {
      const response = await resolveConversation(activeConversation.id, resolveNotes);

      if (response.success) {
        setActiveConversation(prev => prev ? {
          ...prev,
          status: 'resolved',
          resolved_at: new Date().toISOString(),
          resolution_notes: resolveNotes,
        } : null);

        setConversations(prev => prev.map(c =>
          c.id === activeConversation.id
            ? { ...c, status: 'resolved' as const }
            : c
        ));

        setShowResolveModal(false);
        setResolveNotes('');

        const systemMessage: SupportMessage = {
          id: 'system-' + Date.now(),
          conversation_id: activeConversation.id,
          sender_id: 'system',
          sender_name: 'System',
          sender_type: 'system',
          content: `This conversation has been marked as resolved.${resolveNotes ? ` Notes: ${resolveNotes}` : ''}`,
          message_type: 'status_change',
          attachment_url: null,
          attachment_name: null,
          attachment_type: null,
          is_read: 1,
          read_at: null,
          created_at: new Date().toISOString(),
        };
        setMessages(prev => [...prev, systemMessage]);

        fetchStats();
      }
    } catch (error) {
      console.error('Failed to resolve conversation:', error);
    }
  }, [activeConversation, resolveNotes, fetchStats]);

  // Handle SSE events
  const handleNewMessage = useCallback((data: unknown) => {
    const event = data as {
      conversationId: string;
      messageId: string;
      content: string;
      senderName: string;
      senderType: 'user' | 'admin' | 'system';
      createdAt: string;
    };

    if (event.conversationId === activeConversationId.current) {
      const newMessage: SupportMessage = {
        id: event.messageId,
        conversation_id: event.conversationId,
        sender_id: '',
        sender_name: event.senderName,
        sender_type: event.senderType,
        content: event.content,
        message_type: 'text',
        attachment_url: null,
        attachment_name: null,
        attachment_type: null,
        is_read: 1,
        read_at: null,
        created_at: event.createdAt,
      };

      setMessages(prev => {
        if (prev.some(m => m.id === event.messageId)) return prev;
        return [...prev, newMessage];
      });
    }

    setConversations(prev => prev.map(c =>
      c.id === event.conversationId
        ? { ...c, last_message_at: event.createdAt, unread_count: (c.unread_count || 0) + 1 }
        : c
    ));
  }, []);

  // Scroll to bottom
  const scrollToBottom = useCallback(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, []);

  // Scroll when messages change
  useEffect(() => {
    const timer = setTimeout(scrollToBottom, 100);
    return () => clearTimeout(timer);
  }, [messages, scrollToBottom]);

  // Initial fetch
  useEffect(() => {
    fetchStats();
    fetchCategories();
    fetchCannedResponses();
  }, [fetchStats, fetchCategories, fetchCannedResponses]);

  // Fetch conversations when filters change
  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Subscribe to SSE events
  useEffect(() => {
    notificationSSE.subscribe('support:new_message', handleNewMessage);
    return () => {
      notificationSSE.unsubscribe('support:new_message', handleNewMessage);
    };
  }, [handleNewMessage]);

  // Render stat card
  const renderStatCard = (icon: React.ReactNode, value: string | number, label: string, color: string) => (
    <div className={styles.statCard}>
      <div className={styles.statIcon} style={{ backgroundColor: `${color}15`, color }}>
        {icon}
      </div>
      <div className={styles.statContent}>
        <span className={styles.statValue}>{value}</span>
        <span className={styles.statLabel}>{label}</span>
      </div>
    </div>
  );

  // Render conversation card
  const renderConversationCard = (conversation: SupportConversation) => {
    const statusColor = STATUS_COLORS[conversation.status] || '#6b7280';
    const priorityConfig = PRIORITY_CONFIG[conversation.priority] || PRIORITY_CONFIG.normal;

    return (
      <button
        key={conversation.id}
        className={styles.conversationCard}
        onClick={() => openConversation(conversation.id)}
      >
        <div className={styles.cardHeader}>
          <span className={styles.cardSubject}>{conversation.subject}</span>
          {conversation.unread_count && conversation.unread_count > 0 && (
            <span className={styles.unreadBadge}>{conversation.unread_count}</span>
          )}
        </div>

        <div className={styles.cardMeta}>
          <span className={styles.mdaBadge}>
            {Icons.building}
            {conversation.mda_code || 'Unknown'}
          </span>
          <span
            className={styles.statusBadge}
            style={{ backgroundColor: `${statusColor}18`, color: statusColor }}
          >
            {conversation.status.replace('_', ' ')}
          </span>
          {conversation.priority !== 'normal' && (
            <span
              className={styles.priorityBadge}
              style={{ backgroundColor: `${priorityConfig.color}18`, color: priorityConfig.color }}
            >
              {priorityConfig.label}
            </span>
          )}
        </div>

        <div className={styles.cardFooter}>
          <span className={styles.cardUser}>
            {Icons.user}
            {conversation.initiated_by_name}
          </span>
          <span className={styles.cardTime}>
            {formatTimeAgo(conversation.last_message_at || conversation.created_at)}
          </span>
        </div>

        <div className={styles.cardAction}>
          {Icons.externalLink}
          <span>Open Chat</span>
        </div>
      </button>
    );
  };

  // Render message
  const renderMessage = (message: SupportMessage) => {
    const isAdmin = message.sender_type === 'admin';
    const isSystem = message.sender_type === 'system';
    const isAI = message.message_type === 'ai_response';

    if (isSystem) {
      return (
        <div key={message.id} className={styles.systemMessage}>
          <span className={styles.systemMessageContent}>{message.content}</span>
          <span className={styles.systemMessageTime}>{formatTime(message.created_at)}</span>
        </div>
      );
    }

    return (
      <div
        key={message.id}
        className={`${styles.message} ${isAdmin ? styles.adminMessage : styles.userMessage} ${isAI ? styles.aiMessage : ''}`}
      >
        <div className={styles.messageAvatar}>
          {isAI ? Icons.sparkles : isAdmin ? 'A' : 'U'}
        </div>
        <div className={styles.messageContent}>
          <div className={styles.messageBubble}>
            <span className={styles.messageSender}>
              {isAI ? 'AI Assistant' : message.sender_name}
            </span>
            <p className={styles.messageText}>{message.content}</p>
          </div>
          <span className={styles.messageTime}>{formatTime(message.created_at)}</span>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>Support Center</h1>
          <p className={styles.subtitle}>Cross-MDA support management for OHCS institutions</p>
        </div>
        <button
          className={styles.refreshButton}
          onClick={fetchConversations}
          title="Refresh"
        >
          {Icons.refresh}
        </button>
      </div>

      {/* Stats */}
      <div className={styles.stats}>
        {renderStatCard(Icons.inbox, stats?.open ?? 0, 'Open Tickets', '#f59e0b')}
        {renderStatCard(Icons.messageCircle, stats?.assigned ?? 0, 'In Progress', '#006B3F')}
        {renderStatCard(Icons.clock, stats?.avgResponseTime ? `${Math.round(stats.avgResponseTime)}m` : '—', 'Avg Response', '#8b5cf6')}
        {renderStatCard(Icons.star, stats?.avgRating ? stats.avgRating.toFixed(1) : '—', 'CSAT Score', '#10b981')}
      </div>

      {/* Toolbar */}
      <div className={styles.toolbar}>
        <div className={styles.viewTabs}>
          <button
            className={`${styles.viewTab} ${viewMode === 'all' ? styles.activeTab : ''}`}
            onClick={() => setViewMode('all')}
          >
            All Tickets
          </button>
          <button
            className={`${styles.viewTab} ${viewMode === 'queue' ? styles.activeTab : ''}`}
            onClick={() => setViewMode('queue')}
          >
            Queue
            {stats?.open ? <span className={styles.tabBadge}>{stats.open}</span> : null}
          </button>
          <button
            className={`${styles.viewTab} ${viewMode === 'mine' ? styles.activeTab : ''}`}
            onClick={() => setViewMode('mine')}
          >
            My Tickets
          </button>
        </div>

        <div className={styles.toolbarRight}>
          <div className={styles.searchInput}>
            {Icons.search}
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className={styles.clearSearch} onClick={() => setSearchQuery('')}>
                {Icons.x}
              </button>
            )}
          </div>
          <button
            className={`${styles.filterButton} ${showFilters ? styles.active : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            {Icons.filter}
            Filters
          </button>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className={styles.filterPanel}>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="">All Statuses</option>
            <option value="open">Open</option>
            <option value="assigned">Assigned</option>
            <option value="pending_user">Pending User</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>

          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="">All Priorities</option>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="normal">Normal</option>
            <option value="low">Low</option>
          </select>

          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Conversations Grid */}
      <div className={styles.conversationsGrid}>
        {loadingConversations ? (
          <div className={styles.loadingState}>
            <div className={styles.spinner} />
            <p>Loading conversations...</p>
          </div>
        ) : conversations.length === 0 ? (
          <div className={styles.emptyState}>
            <span className={styles.emptyIcon}>{Icons.inbox}</span>
            <p>
              {viewMode === 'queue'
                ? 'Queue is empty — all caught up!'
                : viewMode === 'mine'
                ? 'No conversations assigned to you'
                : 'No conversations found'}
            </p>
          </div>
        ) : (
          conversations.map(renderConversationCard)
        )}
      </div>

      {/* Chat Modal */}
      {showChatModal && (
        <div className={styles.chatModalOverlay} onClick={closeChatModal}>
          <div className={styles.chatModal} onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className={styles.chatModalHeader}>
              <div className={styles.chatModalHeaderInfo}>
                {activeConversation ? (
                  <>
                    <h2 className={styles.chatModalTitle}>{activeConversation.subject}</h2>
                    <div className={styles.chatModalMeta}>
                      <span className={styles.mdaBadge}>
                        {Icons.building}
                        {activeConversation.mda_name || activeConversation.mda_code || 'Unknown'}
                      </span>
                      <span
                        className={styles.statusBadge}
                        style={{
                          backgroundColor: `${STATUS_COLORS[activeConversation.status]}18`,
                          color: STATUS_COLORS[activeConversation.status]
                        }}
                      >
                        {activeConversation.status.replace('_', ' ')}
                      </span>
                    </div>
                  </>
                ) : (
                  <span>Loading...</span>
                )}
              </div>
              <div className={styles.chatModalActions}>
                {activeConversation?.status === 'open' && (
                  <button className={styles.claimButton} onClick={handleClaim}>
                    {Icons.headphones}
                    Claim
                  </button>
                )}
                {(activeConversation?.status === 'assigned' || activeConversation?.status === 'pending_user') && (
                  <button className={styles.resolveButton} onClick={() => setShowResolveModal(true)}>
                    {Icons.checkCircle}
                    Resolve
                  </button>
                )}
                <button className={styles.closeModalButton} onClick={closeChatModal}>
                  {Icons.x}
                </button>
              </div>
            </div>

            {/* User Info Bar */}
            {activeConversation && (
              <div className={styles.userInfoBar}>
                <div className={styles.userInfo}>
                  <span className={styles.userAvatar}>
                    {activeConversation.initiated_by_name.charAt(0)}
                  </span>
                  <div className={styles.userDetails}>
                    <span className={styles.userName}>{activeConversation.initiated_by_name}</span>
                    <span className={styles.userEmail}>{activeConversation.initiated_by_email}</span>
                  </div>
                </div>
                {activeConversation.assigned_to_name && (
                  <div className={styles.assignedInfo}>
                    Assigned to: <strong>{activeConversation.assigned_to_name}</strong>
                  </div>
                )}
              </div>
            )}

            {/* Messages */}
            <div className={styles.chatModalMessages} ref={messagesContainerRef}>
              {loadingMessages ? (
                <div className={styles.loadingState}>
                  <div className={styles.spinner} />
                  <p>Loading messages...</p>
                </div>
              ) : (
                <>
                  {messages.map(renderMessage)}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* Input Area */}
            {activeConversation && activeConversation.status !== 'resolved' && activeConversation.status !== 'closed' && (
              <div className={styles.chatModalInput}>
                {showCannedResponses && (
                  <div className={styles.cannedResponsesPanel}>
                    <div className={styles.cannedResponsesHeader}>
                      <span>Quick Responses</span>
                      <button onClick={() => setShowCannedResponses(false)}>{Icons.x}</button>
                    </div>
                    <div className={styles.cannedResponsesList}>
                      {cannedResponses.map(response => (
                        <button
                          key={response.id}
                          className={styles.cannedResponseItem}
                          onClick={() => handleUseCannedResponse(response)}
                        >
                          <span className={styles.cannedResponseTitle}>{response.title}</span>
                          <span className={styles.cannedResponsePreview}>
                            {response.content.substring(0, 80)}...
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className={styles.inputRow}>
                  <button
                    className={styles.cannedButton}
                    onClick={() => setShowCannedResponses(!showCannedResponses)}
                    title="Quick responses"
                  >
                    {Icons.zap}
                  </button>
                  <textarea
                    className={styles.messageInputField}
                    placeholder="Type your reply..."
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    rows={2}
                  />
                  <button
                    className={styles.sendButton}
                    onClick={handleSendMessage}
                    disabled={!messageInput.trim() || sending}
                  >
                    {Icons.send}
                  </button>
                </div>
              </div>
            )}

            {/* Resolved Banner */}
            {activeConversation && (activeConversation.status === 'resolved' || activeConversation.status === 'closed') && (
              <div className={styles.resolvedBanner}>
                {Icons.checkCircle}
                <span>This conversation has been {activeConversation.status}</span>
                {activeConversation.rating && (
                  <span className={styles.ratingDisplay}>Rating: {activeConversation.rating}/5</span>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Resolve Modal */}
      {showResolveModal && (
        <div className={styles.modalOverlay} onClick={() => setShowResolveModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>Mark as Resolved</h3>
            <p className={styles.modalText}>
              This will close the ticket and notify the user.
            </p>
            <textarea
              className={styles.modalTextarea}
              placeholder="Add resolution notes (optional)..."
              value={resolveNotes}
              onChange={(e) => setResolveNotes(e.target.value)}
              rows={4}
            />
            <div className={styles.modalActions}>
              <button className={styles.modalCancel} onClick={() => setShowResolveModal(false)}>
                Cancel
              </button>
              <button className={styles.modalConfirm} onClick={handleResolve}>
                {Icons.checkCircle}
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
